import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';

import { intlMessage } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { selectIsMobile } from 'selectors/ui';

import messages from './MobileActionSheet.messages';
import './MobileActionSheet.scss';

const { block, element } = bem('mobile-action-sheet');

const MobileActionSheet = ({ isOpen, onClose, title, options, align = 'left', cancelLabel, children, className }) => {
  const intl = useIntl();
  const isMobile = useSelector(selectIsMobile);

  useEffect(() => {
    if (!isOpen) return undefined;

    const { scrollY } = window;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  if (!isMobile) {
    return null;
  }

  const handleOptionClick = option => {
    option.onClick?.();
    onClose();
  };

  const cancelText = cancelLabel ? intl.formatMessage(cancelLabel) : intl.formatMessage(messages.cancel);
  let groups = [];
  if (options) {
    groups = Array.isArray(options[0]) ? options : [options];
  }
  const getGroupKey = (group, idx) => {
    if (group.length > 0 && group[0].danger) return 'danger';
    if (group.length > 0 && typeof group[0].label === 'string') return group[0].label;
    return `group-${idx}`;
  };

  const renderOption = (option, index) => (
    <button
      key={index}
      type="button"
      className={element('option', {
        danger: option.danger,
        'align-center': align === 'center',
        'align-right': align === 'right',
      })}
      onClick={() => handleOptionClick(option)}>
      {option.icon && (
        <span className={element('option-icon')}>
          <Isvg src={option.icon} />
        </span>
      )}
      {option.label}
    </button>
  );

  return createPortal(
    <div className={block(className)}>
      <CSSTransition classNames={element('overlay')} in={isOpen} timeout={300} unmountOnExit>
        <div className={element('overlay')} data-testid="action-sheet-overlay" onClick={onClose} role="presentation" />
      </CSSTransition>
      <CSSTransition classNames={element('sheet')} in={isOpen} timeout={300} unmountOnExit>
        <div className={element('sheet')}>
          {children ?? (
            <>
              {groups.map((group, groupIndex) => (
                <div key={getGroupKey(group, groupIndex)} className={element('options')}>
                  {groupIndex === 0 && title && (
                    <div
                      className={element('title', {
                        'align-center': align === 'center',
                        'align-right': align === 'right',
                      })}>
                      {title}
                    </div>
                  )}
                  {group.map(renderOption)}
                </div>
              ))}
              <button type="button" className={element('cancel')} onClick={onClose}>
                {cancelText}
              </button>
            </>
          )}
        </div>
      </CSSTransition>
    </div>,
    document.body
  );
};

const optionShape = PropTypes.shape({
  icon: PropTypes.string,
  label: PropTypes.node,
  onClick: PropTypes.func,
  danger: PropTypes.bool,
});

MobileActionSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  options: PropTypes.oneOfType([PropTypes.arrayOf(optionShape), PropTypes.arrayOf(PropTypes.arrayOf(optionShape))]),
  cancelLabel: intlMessage,
  children: PropTypes.node,
  className: PropTypes.string,
};

export default MobileActionSheet;
